<template>
  <TableList
    v-rect-loading="loading"
    class="source-channel-page"
    row-key="id"
    noAction
    :title="$t(`${i18nPrefix}.tab.channelManagement`)"
    :listData="sourceChannelList"
    :tableColumns="tableColumns"
    :pagination="pagination"
    @create="handelCreateChannel"
    @pagination-change="handlePaginationChange"
  >
    <template #filter>
      <div class="header-filter">
        <span class="company-select">
          <el-select
            v-model="selectedCompanyId"
            filterable
            clearable
            ref="companySelect"
            :loading="rootCompany.loading"
            :placeholder="$t(`${i18nPrefix}.label.defaultChannel`)"
            :no-data-text="$t(`${i18nPrefix}.label.pleaseStartTyping`)"
            :filter-method="filterRootCompany"
            @change="handelSelectCompany"
            @clear="handleSelectClear"
            @visible-change="handleVisibleChange"
          >
            <el-option
              v-for="item in rootCompany.showData"
              :key="item.id"
              :label="item.label"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </span>
      </div>
      <div class="header-filter">
        <span class="status-select">
          <el-select v-model="selectedStatus" @change="handleStatusSelect">
            <el-option
              v-for="item in statusList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
            </el-option>
          </el-select>
        </span>
      </div>
      <div class="header-filter">
        <el-input
          v-model="keyword"
          class="search-input"
          clearable
          :title="$t(`${i18nPrefix}.placeholder.search`)"
          :placeholder="$t(`${i18nPrefix}.placeholder.search`)"
          @input="handleKeywordsChange"
        >
        </el-input>
      </div>
    </template>
    <template #status="{ data, row }">
      <el-switch :value="!data" @change="onStatusChange($event, row)"></el-switch>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'

export default {
  components: {
    TableList
  },
  props: {

  },
  data () {
    return {
      i18nPrefix: 'admin.customer.sourceChannelManagement',
      loading: false,
      sourceChannelList: [],
      selectedStatus: 0,
      keyword: null,
      selectedCompanyId: null,
      keywordsSearchDebounce: null,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      rootCompany: {
        data: null,
        loading: false,
        filterData: null,
        showData: [],
        hasLoad: false,
        page: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    tableColumns () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.label.affiliatedCompany`),
          dataKey: 'rootCompanyName',
          showOverflowTooltip: true,
          width: 50
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.chineseName`),
          dataKey: 'cn_name',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.englishName`),
          dataKey: 'name',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'is_disabled',
          slotName: 'status',
          width: 20
        }
      ]
    },
    statusList () {
      return [
        { value: 0, label: this.$t(`${this.i18nPrefix}.label.all`) },
        { value: 1, label: this.$t(`${this.i18nPrefix}.label.enable`) },
        { value: 2, label: this.$t(`${this.i18nPrefix}.label.disable`) }
      ]
    },
    queryCompany () {
      return this.$route.query.company
    }
  },
  created () {
    if (!this.rootCompany.hasLoad) {
      this.loadRootCompanyList()
    }
  },
  methods: {
    ...mapActions('superAdmin', ['getSuperAdminCompanyList']),
    ...mapActions('sourceCode', ['getSourceChannelList', 'updateSourceChannelStatus']),
    async loadSourceChannelList (reload) {
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      const params = {
        status: this.selectedStatus,
        company_id: this.selectedCompanyId || -1,
        page: this.pagination.page,
        page_size: this.pagination.pageSize,
        keyword: this.keyword
      }
      this.loading = true
      try {
        const resp = await this.getSourceChannelList(params)
        if (resp && resp.result) {
          const channelList = _.map(resp.result, item => addI18nProperty(item, 'rootCompanyName', 'root_company.cn_name', 'root_company.name'))
          this.sourceChannelList = channelList
          this.pagination.total = resp.total
        }
      } finally {
        this.loading = false
      }
    },
    async loadRootCompanyList () {
      if (this.rootCompany.loading) {
        return
      }
      this.loading = true
      this.rootCompany.loading = true
      try {
        const params = {
          only_root_company: 1
        }
        const resp = await this.getSuperAdminCompanyList(params)
        if (resp && resp.result) {
          const rootCompanyList = resp.result
          _.forEach(rootCompanyList, rootCompany => {
            addI18nProperty(rootCompany, 'rootCompanyName', 'cn_name', 'name')
            const rootCompanyName = rootCompany.rootCompanyName
            const label = `${rootCompany.id} - ${rootCompanyName ? `${rootCompanyName}` : ''}`
            this.$set(rootCompany, 'label', label)
          })
          this.rootCompany.data = rootCompanyList
          this.rootCompany.hasLoad = true
        }
      } finally {
        this.loading = false
        this.rootCompany.loading = false
      }
    },
    async onStatusChange (status, row) {
      const data = { ...row }
      data.is_disabled = !status
      this.loading = true
      try {
        const resp = await this.updateSourceChannelStatus(data)
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
          row.is_disabled = resp.is_disabled
        }
      } finally {
        this.loading = false
      }
    },
    async filterRootCompany (keyword) {
      const rootCompany = this.rootCompany
      if (!_.trim(keyword) || rootCompany.loading) {
        return
      }
      rootCompany.loading = true
      rootCompany.page = 1
      const filterData = _.filter(rootCompany.data, rootCompany => {
        return _.includes(_.toUpper(rootCompany.label), _.toUpper(keyword))
      })
      rootCompany.filterData = filterData
      rootCompany.showData = filterData.slice(0, rootCompany.pageSize)
      const elSelect = this.$refs.companySelect
      this.$nextTick(() => {
        const el = elSelect?.popperElm.querySelector('.el-select-dropdown__wrap')
        if (el && !el.hasAttribute('filter-scroll-listened')) {
          el.setAttribute('filter-scroll-listened', true)
          el.addEventListener('scroll', () => {
            const rootCompany = this.rootCompany
            const condition = el.scrollHeight - el.scrollTop - 20 <= el.clientHeight
            if (condition) {
              if (rootCompany.filterData && rootCompany.page * rootCompany.pageSize > rootCompany.filterData.length) {
                return
              }
              const data = rootCompany.filterData.slice(rootCompany.page * rootCompany.pageSize, (rootCompany.page + 1) * rootCompany.pageSize)
              rootCompany.showData.push(...data)
              rootCompany.page += 1
            }
          })
        }
      })
      rootCompany.loading = false
    },
    handelSelectCompany () {
      this.$router.push({ query: { ...this.$route.query, company: this.selectedCompanyId } })
    },
    handleSelectClear () {
      this.$router.push({ query: { ...this.$route.query, company: -1 } })
    },
    handleVisibleChange () {
      this.rootCompany.showData = []
    },
    handelCreateChannel () {
      this.$router.push({ path: '/v-admin/client-admin/source-channel/management/channel/add', query: this.$route.query })
    },
    handleStatusSelect () {
      this.loadSourceChannelList(true)
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(this.handleSearch, 500)
      }
      this.keywordsSearchDebounce()
    },
    handleSearch () {
      this.loadSourceChannelList(true)
    },
    handlePaginationChange () {
      this.loadSourceChannelList()
    }
  },
  watch: {
    $route: {
      immediate: true,
      async handler () {
        let companyId = this.queryCompany
        if (!companyId || companyId === '-1') {
          this.selectedCompanyId = null
          this.loadSourceChannelList(true)
          return
        }
        if (!this.rootCompany.hasLoad) {
          await this.loadRootCompanyList()
        }
        companyId = parseInt(companyId)
        if (this.selectedCompanyId !== companyId) {
          this.selectedCompanyId = companyId
          const rootCompany = _.find(this.rootCompany.data, { id: this.selectedCompanyId })
          this.rootCompany.showData = [rootCompany]
        }
        this.loadSourceChannelList(true)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.source-channel-page {
  padding: 0;

  .header-filter {
    display: inline-block;
    vertical-align: middle;
    margin-bottom: 10px;

    .text {
      margin-right: 10px;
      display: inline-block;
      vertical-align: middle;
    }

    .company-select {
      display: inline-block;
      vertical-align: middle;
    }

    .status-select {
      display: inline-block;
      vertical-align: middle;
    }

    ::v-deep {
      .el-input__inner {
        border: 1px solid #c0c4cc;
      }

      .el-input-group__append {
        border-left: none;
        padding: 0 5px;
        background-color: #fff;
      }
    }

    .icon-search {
      cursor: pointer;
      font-size: 18px;
    }
  }

  ::v-deep {
    .list-head {
      height: auto;
      flex-wrap: wrap;
      justify-content: flex-end;
      margin-bottom: 10px;

      .title {
        margin-bottom: 10px;
      }

      .btn-container {
        display: flex;
        margin-left: 10px;
        margin-bottom: 10px;
      }
    }
  }
}
</style>
